import { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import useActiveBatch from "@/hooks/useActiveBatch";
import { useBatch, stockText } from "@/context/BatchContext";
import { BatchAlertSignup } from "@/components/BatchAlertSignup";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "biga_batch_popup_dismissed";

function Countdown({ dropTime }) {
  const [now, setNow] = useState(Date.now());
  const ref = useRef(null);
  useEffect(() => {
    ref.current = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(ref.current);
  }, []);
  const diff = Math.max(0, new Date(dropTime).getTime() - now);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function BatchPopup() {
  const batch = useActiveBatch();
  const { display } = useBatch();
  const [visible, setVisible] = useState(false);
  const dismissed = useRef(false);

  useEffect(() => {
    if (dismissed.current) return;
    if (sessionStorage.getItem(SESSION_KEY)) { dismissed.current = true; return; }
    if (!batch) return;
    const timer = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(timer);
  }, [batch]);

  const close = () => {
    setVisible(false);
    dismissed.current = true;
    sessionStorage.setItem(SESSION_KEY, "1");
  };

  if (!visible || !batch) return null;

  const { status, remaining_trays, drop_time, pizzas_per_tray } = batch;
  const pizzas = batch.pizzas_left ?? Math.round(remaining_trays * (pizzas_per_tray || 4));

  let headline, body, ctaLabel, ctaOnClick;

  if (status === "fermenting" || status === "scheduled") {
    headline = display.sold_out_label;
    body = (
      new Date(drop_time).getTime() > Date.now()
        ? <>Fresh pizzas back in <span className="font-mono font-black"><Countdown dropTime={drop_time} /></span></>
        : <>Fresh pizzas back soon.</>
    );
    ctaLabel = "signup";
  } else if (status === "live_drop") {
    headline = stockText(pizzas > 0 ? pizzas : 0, display);
    body = "Fresh 72-hour BIGA pizzas, ready to order.";
    ctaLabel = "Order Now";
    ctaOnClick = () => { close(); document.getElementById("menu")?.scrollIntoView({ behavior: "smooth" }); };
  } else if (status === "sold_out") {
    headline = display.sold_out_label;
    body = "Crafted with patience, gone in minutes. We can tell you when they are back.";
    ctaLabel = "signup";
  } else {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-5" onClick={close} data-testid="batch-popup-overlay">
      <div
        onClick={e => e.stopPropagation()}
        className="w-full max-w-sm bg-[#FAF7F2] rounded-2xl p-7 relative animate-in fade-in duration-300"
        style={{ animation: "fadeIn .3s ease" }}
        data-testid="batch-popup"
      >
        <style>{`@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>

        <button onClick={close} className="absolute top-4 right-4 text-[#1A1A1A]/40 hover:text-[#1A1A1A]" data-testid="batch-popup-close">
          <X size={20} />
        </button>

        <img
          src={`${BACKEND}/api/assets/biga-orange-logo`}
          alt="BIGA"
          width={56}
          height={56}
          className="rounded-md mb-5"
        />

        <h2 className="font-display font-black text-xl text-[#1A1A1A] leading-tight" data-testid="batch-popup-headline">
          {headline}
        </h2>
        <p className="text-sm text-[#1A1A1A]/60 mt-2 leading-relaxed" data-testid="batch-popup-body">
          {body}
        </p>

        {ctaLabel === "signup" ? (
          <BatchAlertSignup onDone={close} />
        ) : ctaOnClick ? (
          <button
            onClick={ctaOnClick}
            data-testid="batch-popup-cta"
            className="mt-6 w-full py-3 rounded-full font-bold text-sm text-white bg-[#D97706] hover:bg-[#B45309] transition-colors"
          >
            {ctaLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}
